import { useCallback, useEffect, useMemo, useState } from "react";
import { IFormCustomValidation } from "../../../@vendor/utils/interfaces/form.interface";
import { useDebounce } from "usehooks-ts";
import { CountryPhoneInputValue } from "antd-country-phone-input";
import AuthRepository from "../../../repositories/AuthRepository";
import { AxiosError } from "axios";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { isValidEmail } from "../../../@vendor/utils";

type UseGuestFieldValidationOptions = {
  email: string | undefined;
  phoneValue: CountryPhoneInputValue | undefined;
  manageStepValidator: (
    step: number,
    fn?: () => string | null
  ) => (() => void) | string | null;
  step?: number;
  enabled?: boolean;
};

export const useGuestFieldValidation = ({
  email,
  phoneValue,
  manageStepValidator,
  step = 1,
  enabled = false,
}: UseGuestFieldValidationOptions) => {
  const { t } = useTranslation();
  const isPhoneValid = (phoneValue: CountryPhoneInputValue | undefined) => {
    const phoneLength = _.get(phoneValue, "phone.length", 0);
    return phoneLength >= 7 && phoneLength <= 15;
  };
  const [emailValidation, setEmailValidation] = useState<IFormCustomValidation>(
    {
      validateStatus: undefined,
      errorMessage: undefined,
    }
  );
  const [phoneValidation, setPhoneValidation] = useState<IFormCustomValidation>(
    {
      validateStatus: undefined,
      errorMessage: undefined,
    }
  );

  const debounceEmail = useDebounce<string>(email ?? "", 500);
  const debouncePhone = useDebounce<CountryPhoneInputValue | undefined>(
    phoneValue,
    500
  );

  const handleValidateEmail = useCallback(async () => {
    setEmailValidation((s) => ({
      ...s,
      validateStatus: "validating",
      errorMessage: undefined,
    }));

    await AuthRepository.checkEmail({ email: email ?? "" })
      .then(() => {
        setEmailValidation({
          validateStatus: "success",
          errorMessage: undefined,
        });
      })
      .catch((error: AxiosError) => {
        if (error.name !== "CanceledError") {
          setEmailValidation({
            validateStatus: "error",
            errorMessage: t("common.text.emailAlreadyUsed"),
          });
        }
      });
  }, [email]);

  const handleValidatePhone = useCallback(async () => {
    setPhoneValidation((s) => ({
      ...s,
      validateStatus: "validating",
      errorMessage: undefined,
    }));
    const phoneCountry = phoneValue?.code || "";
    const phoneNumber = phoneValue?.phone || "";
    await AuthRepository.checkPhone({
      phone_number: `${String(phoneCountry)} ${phoneNumber}`,
    })
      .then(() => {
        if (isPhoneValid(phoneValue as CountryPhoneInputValue)) {
          setPhoneValidation({
            validateStatus: "success",
            errorMessage: undefined,
          });
        } else {
          setPhoneValidation({
            validateStatus: undefined,
            errorMessage: undefined,
          });
        }
      })
      .catch((error) => {
        if (error.name !== "CanceledError") {
          setPhoneValidation({
            validateStatus: "error",
            errorMessage: t("common.text.phoneAlreadyUsed"),
          });
        }
      });
  }, [phoneValue]);

  useEffect(() => {
    if (!enabled) {
      setEmailValidation({
        validateStatus: undefined,
        errorMessage: undefined,
      });
      return;
    }

    if ((debounceEmail ?? "") !== "") {
      if (isValidEmail(debounceEmail)) {
        handleValidateEmail();
      } else {
        setEmailValidation((s) => ({
          ...s,
          validateStatus: "error",
          errorMessage: t("validation.type.email", {
            item: t("common.text.patientEmail"),
          }),
        }));
      }
    } else {
      setEmailValidation({
        validateStatus: undefined,
        errorMessage: undefined,
      });
    }
  }, [debounceEmail, handleValidateEmail, enabled]);

  useEffect(() => {
    if (!enabled) {
      setPhoneValidation({
        validateStatus: undefined,
        errorMessage: undefined,
      });
      return;
    }

    if (
      debouncePhone !== undefined &&
      isPhoneValid(debouncePhone as CountryPhoneInputValue)
    ) {
      handleValidatePhone();
    } else {
      if (debouncePhone?.phone) {
        setPhoneValidation((s) => ({
          ...s,
          validateStatus: "error",
          errorMessage: t("validation.invalid", {
            item: t("common.text.patientPhoneNumber"),
          }),
        }));
      } else {
        setPhoneValidation({
          validateStatus: undefined,
          errorMessage: undefined,
        });
      }
    }
  }, [debouncePhone, handleValidatePhone, enabled]);

  // Register a step validator which returns combined messages if present
  useEffect(() => {
    if (!enabled) {
      // Ensure validator is not registered and validations are reset when disabled
      const unregister = manageStepValidator(step) as () => void;
      if (typeof unregister === "function") unregister();
      return;
    }

    const validator = () => {
      const errors: string[] = [];

      if (emailValidation.validateStatus === "error") {
        errors.push(emailValidation.errorMessage ?? "Invalid email");
      }
      if (phoneValidation.validateStatus === "error") {
        errors.push(phoneValidation.errorMessage ?? "Invalid phone");
      }

      if (errors.length === 0) return null;
      if (errors.length === 1) return errors[0];

      return "This email address and phone number has been taken by another account";
    };

    const unregister = manageStepValidator(step, validator) as () => void;

    return () => {
      if (typeof unregister === "function") unregister();
    };
  }, [
    emailValidation.validateStatus,
    emailValidation.errorMessage,
    phoneValidation.validateStatus,
    phoneValidation.errorMessage,
    manageStepValidator,
    step,
    enabled,
  ]);

  return useMemo(
    () => ({ emailValidation, phoneValidation }),
    [emailValidation, phoneValidation]
  );
};
